import { useCallback, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { GetJWTHandlerPayload } from '@hqo/hqo-miniapp-client-sdk';
import { useMiniappSdk } from 'app/shared/contexts/miniapp-sdk';
import { useMiniappSdkEventHandler } from 'app/shared/hooks/use-miniapp-sdk';
import { useApi } from 'app/shared/contexts/api';
import { useErrors } from 'app/shared/contexts/errors';
import { ERROR_SESSION_EXPIRED } from 'app/shared/consts/error-codes';
import { getTokenExpiry, isTokenExpired, isFresherToken } from 'app/shared/utils/jwt';

const UNAUTHORIZED_STATUS = 401;

// Give up after this many consecutive refreshes that fail to yield a fresher token.
// Prevents the doomed 401 -> refresh -> (stale/echoed token) -> 401 loop: if the
// host can't mint a genuinely newer token, we stop asking and surface a terminal state
// instead of hammering it forever (see hqos ACCESS-1935).
const MAX_REFRESH_ATTEMPTS = 3;

// If a refresh request goes unanswered this long, release the single-flight lock so a
// later trigger can try again — a lost postMessage must not wedge refresh permanently.
const REFRESH_TIMEOUT_MS = 10000;

// Refresh this far ahead of the token's expiry so we beat it rather than race it.
const EXPIRY_REFRESH_SKEW_MS = 30000;

// Floor for the proactive timer so a very short-lived token can't drive a synchronous
// refresh loop — bounds the rate to at most one refresh per this interval.
const MIN_REFRESH_DELAY_MS = 1000;

// setTimeout stores its delay in a 32-bit int; anything larger overflows and fires
// immediately. Clamp the proactive timer so a far-future exp can't cause a premature refresh.
const MAX_TIMER_DELAY_MS = 2147483647;

// Surfaced under this key so it can be cleared once a refresh succeeds.
const REFRESH_ERROR_KEY = 'token-refresh';

const isUnauthorizedError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object' || !('statusCode' in error)) {
    return false;
  }

  return (error as { statusCode: unknown }).statusCode === UNAUTHORIZED_STATUS;
};

/**
 * Keeps the embedded miniapp's auth token fresh. The host silently re-auths while a
 * tab is backgrounded, leaving the launch token expired; the webview is never
 * re-launched, so the miniapp must notice its own creds are stale and pull a new
 * token via the SDK (`refreshToken` -> `refreshTokenResponse`), then thread it back in.
 *
 * Hardening:
 *  - single-flight: only one refresh in flight at a time (with a timeout fallback);
 *  - echo-defeat: a response is only accepted if the returned token is genuinely
 *    fresher, so a host that echoes the stale token can't keep the loop alive;
 *  - terminal give-up: after MAX_REFRESH_ATTEMPTS fruitless refreshes, stop and raise
 *    a session-expired error instead of looping forever.
 */
export const useTokenRefresh = (): void => {
  const sdk = useMiniappSdk();
  const authToken = useApi('authToken');
  const setAuthToken = useApi('setAuthToken');
  const queryClient = useQueryClient();
  const { addGenericError, hideGenericErrors } = useErrors();

  const inFlightRef = useRef(false);
  const attemptsRef = useRef(0);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const proactiveCheckedRef = useRef(false);
  // Monotonic id for the in-flight refresh. Responses from the SDK are NOT correlated to a
  // specific request, so a late response (or a superseding one) bumps this to invalidate the
  // previous attempt's pending timeout — stopping it from clearing/terminal-handling the wrong slot.
  const generationRef = useRef(0);
  // The freshest token we've accepted, tracked in a ref so freshness comparisons don't read the
  // stale render-captured `authToken`. Two uncorrelated responses can land in one tick (before
  // React re-renders `authToken`); comparing both against the ref stops a less-fresh second
  // response from regressing a longer-lived first one. Kept in sync with external auth changes
  // (e.g. a new-session launch token) via the effect below.
  const latestTokenRef = useRef(authToken);

  useEffect(() => {
    latestTokenRef.current = authToken;
  }, [authToken]);

  const releaseLock = useCallback(() => {
    inFlightRef.current = false;

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = undefined;
    }
  }, []);

  // Terminal state: the session can't be refreshed. Surfaces the error screen instead of
  // silently looping on an expired token.
  const raiseSessionExpired = useCallback(() => {
    addGenericError(REFRESH_ERROR_KEY, {
      error: new Error('Auth token refresh failed'),
      errorCode: ERROR_SESSION_EXPIRED,
    });
  }, [addGenericError]);

  // The host never answered. Release the lock, and if we've spent the attempt budget,
  // surface the terminal state HERE — the refreshTokenResponse handler can't, because no
  // response arrived (a dropped postMessage / hung host). Guarded by generation so a stale
  // timeout (whose attempt was already superseded by a response or a newer refresh) is a no-op.
  const onRefreshTimeout = useCallback(
    (generation: number) => {
      if (generation !== generationRef.current) {
        return;
      }

      inFlightRef.current = false;
      timeoutRef.current = undefined;

      if (attemptsRef.current >= MAX_REFRESH_ATTEMPTS) {
        raiseSessionExpired();
      }
    },
    [raiseSessionExpired],
  );

  const requestTokenRefresh = useCallback(() => {
    if (!sdk || inFlightRef.current || attemptsRef.current >= MAX_REFRESH_ATTEMPTS) {
      return;
    }

    inFlightRef.current = true;
    attemptsRef.current += 1;
    generationRef.current += 1;
    const generation = generationRef.current;
    timeoutRef.current = setTimeout(() => onRefreshTimeout(generation), REFRESH_TIMEOUT_MS);
    sdk.refreshToken();
  }, [sdk, onRefreshTimeout]);

  // Host answered a refresh. SDK responses aren't correlated to a specific request, so a late
  // response for a timed-out attempt can arrive while a newer one is in flight (or after it
  // released). Handle by token value, not by which request it "belongs" to.
  useMiniappSdkEventHandler(
    'refreshTokenResponse',
    (eventData) => {
      const { token } = (eventData ?? {}) as Partial<GetJWTHandlerPayload>;

      // Adopt ONLY a genuinely fresher token — whichever request it answers, and even with nothing
      // in flight (a late response for a prior attempt still carries a usable token; never discard
      // it). Any other response (missing/echoed/stale) is IGNORED here: SDK responses are NOT
      // correlated to requests, so attributing one to the current in-flight could release a NEWER
      // refresh's lock/timeout or raise a false SESSION_EXPIRED while a valid refresh is still
      // pending. Failure + terminal are owned SOLELY by the generation-guarded timeout below, so a
      // stale/late response can neither clobber a newer attempt nor trip terminal.
      //
      // Compare against `latestTokenRef` (not the render-captured `authToken`) so a second
      // uncorrelated response in the same tick is measured against what the first one just adopted —
      // otherwise a less-fresh R2 (still fresher than the stale closure) would regress R1.
      if (!token || !isFresherToken(token, latestTokenRef.current)) {
        return;
      }

      latestTokenRef.current = token;
      generationRef.current += 1;
      releaseLock();
      attemptsRef.current = 0;
      setAuthToken(token);
      hideGenericErrors(REFRESH_ERROR_KEY);
      queryClient.invalidateQueries();
    },
    [setAuthToken, hideGenericErrors, queryClient, releaseLock],
  );

  // Trigger 0: the launch token is ALREADY expired at boot — e.g. the host tab sat idle,
  // then the user opened the miniapp. Refresh proactively (once) on mount so we don't burn
  // a 401 and flash the loader before recovering.
  useEffect(() => {
    if (proactiveCheckedRef.current || !sdk) {
      return;
    }

    proactiveCheckedRef.current = true;

    if (isTokenExpired(authToken)) {
      requestTokenRefresh();
    }
  }, [sdk, authToken, requestTokenRefresh]);

  // Trigger 1: tab regained focus — refresh only if the token is actually stale, so we
  // don't spam the host on every focus.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isTokenExpired(authToken)) {
        requestTokenRefresh();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [authToken, requestTokenRefresh]);

  // Trigger 2: proactive expiry timer. A tab can stay OPEN and VISIBLE past the token's
  // expiry with no focus change and no request firing, so none of the edge triggers fire.
  // Schedule a refresh just before the current token expires so an idle, foregrounded
  // miniapp refreshes in place. Reschedules whenever the token changes (i.e. after a
  // successful refresh, for the new token's expiry).
  useEffect(() => {
    const exp = getTokenExpiry(authToken);

    if (exp === null) {
      return undefined;
    }

    // Always schedule via setTimeout with a MIN floor — never refresh synchronously. A token whose
    // remaining life is already under the skew would otherwise fire immediately, and since an
    // accepted token resets attemptsRef and re-runs this effect, a stream of short-lived tokens
    // could hammer refreshToken() back-to-back. The floor bounds that to one refresh per interval;
    // the mount + 401 triggers still handle an already-expired boot token promptly.
    const msUntilRefresh = Math.min(
      Math.max(exp * 1000 - Date.now() - EXPIRY_REFRESH_SKEW_MS, MIN_REFRESH_DELAY_MS),
      MAX_TIMER_DELAY_MS,
    );
    const timer = setTimeout(requestTokenRefresh, msUntilRefresh);

    return () => clearTimeout(timer);
  }, [authToken, requestTokenRefresh]);

  // Trigger 3: safety net for any other expiry path — a query 401s. Gate on 'updated' events so a
  // lingering error in a query's state can't re-trigger on unrelated cache events (observer add/
  // remove, etc.); we only react when a query's state actually transitions.
  useEffect(() => {
    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      if (event?.type === 'updated' && isUnauthorizedError(event.query?.state?.error)) {
        requestTokenRefresh();
      }
    });

    return unsubscribe;
  }, [queryClient, requestTokenRefresh]);
};
